import { useContext } from 'react';

import AJAX from 'common/AJAX';
import {
  type BulkDeleteData,
  BulkEditIdeaOperations,
  type BulkMergeData,
  type BulkMoveData,
  type BulkUpdateOwnerData,
  type BulkUpdateStatusData,
  type CreateIdeaData,
  type CreateIdeaFromInsightData,
  type Idea,
  type IdeaResponse,
  type MergeIdeaData,
  type MergeIdeaRequest,
  type MoveIdeaData,
  type UpdateIdeaData,
} from 'common/api/endpoints/idea';
import { MaxIdeaDescriptionErrorMessage } from 'common/constants/ideas';
import { useOnboardingContext } from 'common/containers/OnboardingContainer';
import { LocationContext, RouterContext } from 'common/containers/RouterContainer';
import useMutation, { type MutationOptions } from 'common/hooks/useMutation';
import { MinimumCreatedIdeasRequired } from 'common/subdomain/admin/AdminOnboarding/InAppOnboarding/constants';
import { IdeaQueriesContext } from 'common/subdomain/admin/v2/containers/IdeaListContainer';
import { useIdeasContext } from 'common/subdomain/admin/v2/containers/IdeasContainer';

import type { QueryParams } from 'common/actions/ideaQueries';
import type { SuccessResponse } from 'common/api/responses';

const defaultErrors = {
  'description length': MaxIdeaDescriptionErrorMessage,
};

export const useCreateIdea = (
  ideaQueryParams: QueryParams,
  options?: MutationOptions<IdeaResponse, CreateIdeaData>,
) => {
  const { reloadIdea } = useIdeasContext();
  const { onboarding, reloadOnboarding } = useOnboardingContext();
  const { invalidateIdeaQueries, upsertIdeas, addIdeaToQuery } = useContext(IdeaQueriesContext);

  return useMutation<IdeaResponse, CreateIdeaData>({
    mutationFn: async (data) => {
      const response = await AJAX.post('/api/ideas/create', data);
      return response;
    },
    ...options,
    isResponseSuccessful: options?.isResponseSuccessful || ((response) => !!response.idea),
    onSuccess: async (data, variables) => {
      if (onboarding.ideaStats.ideaCount < MinimumCreatedIdeasRequired) {
        await reloadOnboarding();
      }

      await invalidateIdeaQueries();
      await upsertIdeas([data.idea]);

      if (variables.parentID) {
        await addIdeaToQuery({ parentID: variables.parentID }, data.idea);
        await reloadIdea({ ideaID: variables.parentID });
      } else {
        await addIdeaToQuery(ideaQueryParams, data.idea);
      }

      options?.onSuccess?.(data, variables);
    },
  });
};

export const useCreateIdeaFromInsight = (
  ideaQueryParams: QueryParams,
  options?: MutationOptions<IdeaResponse, CreateIdeaFromInsightData>,
) => {
  const { reloadIdea } = useIdeasContext();
  const { invalidateIdeaQueries, upsertIdeas, addIdeaToQuery } = useContext(IdeaQueriesContext);

  return useMutation<IdeaResponse, CreateIdeaFromInsightData>({
    mutationFn: async (input) => {
      const response = await AJAX.post('/api/ideas/create_from_insight', input);
      return response;
    },
    ...options,
    isResponseSuccessful: options?.isResponseSuccessful || ((response) => !!response),
    onSuccess: async (data, variables) => {
      await invalidateIdeaQueries();
      await upsertIdeas([data.idea]);

      if (data.idea.parentID) {
        await addIdeaToQuery({ parentID: data.idea.parentID }, data.idea);
        await reloadIdea({ ideaID: data.idea.parentID });
      } else {
        await addIdeaToQuery(ideaQueryParams, data.idea);
      }

      options?.onSuccess?.(data, variables);
    },
  });
};

export const useUpdateIdea = (options?: MutationOptions<IdeaResponse, UpdateIdeaData>) => {
  const router = useContext(RouterContext);
  const location = useContext(LocationContext);
  const { invalidateIdeaQueries, upsertIdeas } = useContext(IdeaQueriesContext);

  return useMutation<IdeaResponse, UpdateIdeaData>({
    mutationFn: async (data) => {
      const response = await AJAX.post('/api/ideas/update', data);
      return response;
    },
    ...options,
    isResponseSuccessful: options?.isResponseSuccessful || ((response) => !!response.idea),
    onSuccess: async (data, variables) => {
      await invalidateIdeaQueries();
      await upsertIdeas([data.idea]);

      // If the title was updated, update the url idea query param to reflect the new urlName
      if (variables.title && location.query.idea && data.idea.urlName !== location.query.idea) {
        router.push({
          pathname: location.pathname,
          query: {
            ...location.query,
            idea: data.idea.urlName,
          },
        });
      }

      options?.onSuccess?.(data, variables);
    },
    errors: {
      ...options?.errors,
      ...defaultErrors,
    },
  });
};

export const useMergeIdea = (options?: MutationOptions<string, MergeIdeaData>) => {
  const { removeIdeas, invalidateIdeaQueries } = useContext(IdeaQueriesContext);

  return useMutation({
    mutationFn: async (data) => {
      const body: MergeIdeaRequest = {
        mergeIdeaID: data.mergeIdea._id,
        mergeIntoIdeaID: data.mergeIntoIdea._id,
      };
      const response = await AJAX.post('/api/ideas/merge', body);
      return response;
    },
    ...options,
    onSuccess: async (data, variables) => {
      await invalidateIdeaQueries();
      await removeIdeas([variables.mergeIdea]);

      options?.onSuccess?.(data, variables);
    },
  });
};

export const useMoveIdea = (options?: MutationOptions<string, MoveIdeaData>) => {
  const { invalidateIdeaQueries } = useContext(IdeaQueriesContext);
  const { reloadIdea } = useIdeasContext();

  return useMutation({
    mutationFn: async (data) => {
      const response = await AJAX.post('/api/ideas/move', {
        ideaID: data.ideaID,
        groupID: data.groupID,
        parentID: data.parentID,
      });
      return response;
    },
    ...options,
    onSuccess: async (data, variables) => {
      await invalidateIdeaQueries();
      await reloadIdea({ ideaID: variables.ideaID });
      if (variables.parentID) {
        await reloadIdea({ ideaID: variables.parentID });
      }

      options?.onSuccess?.(data, variables);
    },
  });
};

export const useDeleteIdea = (options?: MutationOptions<SuccessResponse, Idea>) => {
  const router = useContext(RouterContext);
  const location = useContext(LocationContext);
  const { reloadIdea } = useIdeasContext();
  const { invalidateIdeaQueries, removeIdeas } = useContext(IdeaQueriesContext);

  return useMutation({
    mutationFn: async (idea) => {
      const response = await AJAX.post('/api/ideas/delete', {
        ideaID: idea._id,
      });
      return response;
    },
    ...options,
    onSuccess: async (data, deletedIdea) => {
      await invalidateIdeaQueries();
      await removeIdeas([deletedIdea]);

      if (deletedIdea.parentID) {
        await reloadIdea({ ideaID: deletedIdea.parentID });
      }

      if (location.query.idea && deletedIdea.urlName === location.query.idea) {
        router.push({
          pathname: location.pathname,
          query: {
            ...location.query,
            idea: undefined,
          },
        });
      }

      options?.onSuccess?.(data, deletedIdea);
    },
  });
};

export const useBulkUpdateIdeas = (
  options?: MutationOptions<
    SuccessResponse,
    BulkMergeData | BulkMoveData | BulkUpdateStatusData | BulkUpdateOwnerData | BulkDeleteData
  >,
) => {
  const { reloadIdea } = useIdeasContext();
  const { invalidateIdeaQueries, removeIdeas } = useContext(IdeaQueriesContext);

  return useMutation({
    mutationFn: async (data) => {
      const response = await AJAX.post('/api/ideas/bulkEdit', data);
      return response;
    },
    ...options,
    onSuccess: async (data, variables) => {
      await invalidateIdeaQueries();

      switch (variables.operationType) {
        case BulkEditIdeaOperations.move:
          await Promise.all(variables.ideaIDs.map((ideaID) => reloadIdea({ ideaID })));
          if (variables.parentID) {
            await reloadIdea({ ideaID: variables.parentID });
          }
          break;
        case BulkEditIdeaOperations.updateStatus:
        case BulkEditIdeaOperations.updateOwner:
          await Promise.all(variables.ideaIDs.map((ideaID) => reloadIdea({ ideaID })));
          break;
        case BulkEditIdeaOperations.delete:
        case BulkEditIdeaOperations.merge:
          await removeIdeas(variables.ideaIDs.map((ideaID) => ({ _id: ideaID }) as Idea));
          break;
      }

      options?.onSuccess?.(data, variables);
    },
  });
};
